import * as React from 'react'

import { CheckIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import {
  GroupHeader,
  GroupHeaderTrigger,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import { useListSelectorContext } from './ListSelectorContext'
import {
  IListSelectorGroupHeaderInfo,
  IListSelectorGroupSelection,
  IListSelectorItem,
} from './types'

interface IListSelectorRowProps {
  isSelected: boolean
  onToggle: () => void
  /** Shown and counted but not pickable: no focus, no toggle - so single mode stays open. */
  disabled?: boolean
  /** data-testid for the row (opt-in; e2e hook). */
  testId?: string
  /** Replaces the content wrapper's classes — e.g. to let content fill the row. */
  contentClassName?: string
  children: React.ReactNode
}

export function ListSelectorRow({
  isSelected,
  onToggle,
  disabled,
  testId,
  contentClassName,
  children,
}: IListSelectorRowProps): React.ReactElement {
  const ctx = useListSelectorContext()
  const isSingle = ctx.selectionMode === 'single'

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? undefined : 0}
      data-testid={testId}
      className={classList({
        'tw:flex tw:items-center tw:gap-2.5 tw:min-h-7 tw:py-1 tw:pl-1.5 tw:pr-2 tw:rounded-md tw:outline-none tw:text-body-md tw:text-popover-foreground tw:focus-visible:bg-accent-soft tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring': true,
        'tw:cursor-pointer tw:hover:bg-accent-soft': !disabled,
        'tw:opacity-50 tw:cursor-not-allowed': !!disabled,
        'tw:bg-accent-soft': isSelected,
      })}
      onClick={disabled ? undefined : onToggle}
      onKeyDown={
        disabled
          ? undefined
          : e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle()
              }
            }
      }
    >
      {!isSingle && (
        // Mirrors the option's state; the row is the only control.
        <Checkbox
          checked={isSelected}
          displayOnly={true}
          aria-hidden={true}
          tabIndex={-1}
        />
      )}
      <div
        className={
          contentClassName ??
          'tw:flex tw:items-center tw:gap-2.5 tw:min-w-0 tw:flex-1'
        }
      >
        {children}
      </div>
      {isSingle && (
        <CheckIcon
          aria-hidden="true"
          className={classList({
            'tw:size-4 tw:shrink-0': true,
            'tw:invisible': !isSelected,
          })}
        />
      )}
    </div>
  )
}

/** Per-band additions to the default group header, from `ListSelector.List`'s getGroupHeaderProps. */
export interface IListSelectorGroupHeaderExtras {
  /** Overrides the label's colour only - e.g. to mute a band that is inactive. */
  labelClassName?: string
  /** Rendered after the expand trigger, at the band's end - outside it, so it can be interactive. */
  trailing?: React.ReactNode
}

interface IListSelectorGroupHeaderProps<
  T extends IListSelectorItem,
> extends IListSelectorGroupHeaderExtras {
  group: IListSelectorGroupHeaderInfo<T>
  onToggle: () => void
  selection: IListSelectorGroupSelection
  onGroupCheckbox: (e?: React.MouseEvent) => void
  /** Separates the band from the rows of the group above it. */
  hasTopBorder?: boolean
  children?: React.ReactNode
}

export function ListSelectorGroupHeader<T extends IListSelectorItem>({
  group,
  onToggle,
  selection,
  onGroupCheckbox,
  hasTopBorder,
  labelClassName,
  trailing,
  children,
}: IListSelectorGroupHeaderProps<T>): React.ReactElement {
  const ctx = useListSelectorContext()
  const isSingle = ctx.selectionMode === 'single'

  return (
    // Wrapped so CardList's last-row `[&>*]:border-b-0` lands here, keeping the band's bottom border.
    <div>
      <GroupHeader
        role="group"
        aria-label={group.label}
        className={classList({ 'tw:border-t': !!hasTopBorder })}
      >
        {!isSingle && (
          <Checkbox
            checked={selection.allSelected}
            indeterminate={selection.someSelected && !selection.allSelected}
            aria-label={Localization.translator.selectX(group.label)}
            disabled={!selection.hasSelectable}
            onCheckedChange={() => onGroupCheckbox()}
          />
        )}
        <GroupHeaderTrigger
          expanded={!group.isCollapsed}
          onClick={onToggle}
          ariaLabel={group.label}
          leading={<GroupIndent depth={group.depth} />}
          labelIcon={children}
          label={group.label}
          labelClassName={labelClassName}
          count={group.count}
        />
        {trailing}
      </GroupHeader>
    </div>
  )
}
